// src/pages/Profile.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import Loading from '../components/Loading';
import { User, Package, RefreshCw, BookOpen, Coffee, Heart, LogOut } from 'lucide-react';
import { authService } from '../authService';
import { AUTH_CONFIG } from '../config';

const Profile = () => {
  const { user, profile: authProfile, loading, signOut, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [greeting, setGreeting] = useState('');

  // Set greeting based on time of day with emoji
  useEffect(() => {
    const hour = new Date().getHours();
    let greetingMessage = '';
    
    if (hour >= 5 && hour < 12) {
      greetingMessage = 'Buongiorno';
    } else if (hour >= 12 && hour < 18) {
      greetingMessage = 'Buon pomeriggio';
    } else if (hour >= 18 && hour < 22) {
      greetingMessage = 'Buona sera';
    } else {
      greetingMessage = 'Buona tarda notte';
    }
    
    setGreeting(greetingMessage);
  }, []);

  // Fetch user info using auth context profile instead of API
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!user) return;
      
      try {
        setIsLoading(true);
        
        // Use the profile from auth context if available
        if (authProfile) {
          setUserInfo({
            user: authProfile,
            purchase_stats: authProfile.purchase_stats || { total_purchases: 0 }
          });
          setIsLoading(false);
          return;
        }
        
        // If no authProfile, use basic user info
        // This avoids calling an endpoint that doesn't exist
        setUserInfo({
          user: {
            id: user.id,
            email: user.email,
            name: user.email?.split('@')[0] || 'Utente'
          },
          purchase_stats: { total_purchases: 0 }
        });
        
      } catch (error) {
        console.error('Error setting user info:', error);
        setError(error.message || 'Error loading user information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserInfo();
  }, [user, authProfile]);

  // Function to refresh user info
  const refreshUserInfo = async () => {
    if (!user) return;
    
    setIsRefreshing(true);
    try {
      // Instead of calling an endpoint, just use the auth profile data
      if (authProfile) {
        setUserInfo({
          user: authProfile,
          purchase_stats: authProfile.purchase_stats || { total_purchases: 0 }
        });
      } else {
        setUserInfo({
          user: {
            id: user.id,
            email: user.email,
            name: user.email?.split('@')[0] || 'Utente'
          },
          purchase_stats: { total_purchases: 0 }
        });
      }
      
      // You could also refresh the JWT token here if needed
      
    } catch (error) {
      console.error('Error refreshing user info:', error);
      setError(error.message || 'Error refreshing user information');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle logout with proper error handling
  const handleLogout = async () => {
    try {
      // Show loading state
      setIsRefreshing(true);
      
      // Perform logout
      const { error } = await signOut();
      
      if (error) {
        throw new Error(error);
      }
      
      // Force token clearing in case API call succeeded but UI didn't update
      authService.removeToken();
      
      // Redirect to home page
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      
      // Force token clearing even on error
      authService.removeToken();
      
      // Still redirect to home page
      navigate('/');
    }
  };

  // If the authentication is still loading, show a spinner
  if (loading || isLoading) {
    return <Loading message="Caricamento del tuo profilo..." />;
  }

  // This should not happen due to ProtectedRoute, but as a safety check
  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-content py-12 px-4 bg-gradient-to-b from-green-50 to-white">
        <div className="text-center bg-white p-8 rounded-xl shadow-sm border border-green-100 max-w-md">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Autenticazione Richiesta</h2>
          <p className="mb-6 text-gray-600">Devi effettuare l'accesso per visualizzare questa pagina.</p>
          <button 
            onClick={() => navigate('/login')}
            className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-full shadow-sm hover:shadow-md hover:bg-green-700 transition-all"
          >
            Accedi
          </button>
        </div>
      </div>
    );
  }

  // If there was an error fetching user info
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-content py-12 px-4 bg-gradient-to-b from-green-50 to-white">
        <div className="text-center bg-white p-8 rounded-xl shadow-sm border border-red-100 max-w-md">
          <h2 className="text-2xl font-bold text-red-500 mb-4">Errore di Caricamento</h2>
          <p className="mb-6 text-gray-600">{error}</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button 
              onClick={refreshUserInfo}
              className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-full shadow-sm hover:shadow-md hover:bg-green-700 transition-all"
              disabled={isRefreshing}
            >
              <RefreshCw size={18} className={`mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Riprovo...' : 'Riprova'}
            </button>
            <button 
              onClick={handleLogout}
              className="inline-flex items-center px-6 py-3 bg-red-500 text-white font-medium rounded-full shadow-sm hover:shadow-md hover:bg-red-600 transition-all"
            >
              Disconnetti
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Get user data from the response or from auth context
  const profile = userInfo?.user || authProfile || {};
  const purchaseStats = userInfo?.purchase_stats || { total_purchases: 0 };

 
 
  return (
    <div className="min-h-content py-12 px-4 sm:px-6 lg:px-8 bg-gray-50 bg-gradient-to-b from-green-50 to-white">
      <div className="max-w-4xl mx-auto">
        {/* Welcome Banner with Time-based Greeting */}
        <div className="bg-gradient-to-br from-green-100 via-green-50 to-white text-green-800 rounded-xl shadow-md mb-8 overflow-hidden border border-green-100">
          <div className="flex flex-col md:flex-row items-center">
            <div className="p-8 md:p-10 flex-1">
              <div className="flex items-center mb-3">
                <Coffee size={24} className="text-green mr-2" />
                <span className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">
                  {greeting}
                </span>
              </div>
              <h1 className="text-3xl font-bold mb-3 text-gray-800">
                Ciao, {profile?.name || user.email?.split('@')[0] || 'Utente'}!
              </h1>
              <p className="mb-5 text-gray-600">Benvenuto nel tuo spazio personale</p>
              
              {/* Content Info Section moved into welcome banner */}
              <div className="mt-6 bg-white p-5 rounded-xl shadow-sm border border-green-50">
                <h3 className="text-base font-medium text-gray-800 flex items-center mb-4">
                  <BookOpen size={16} className="mr-2 text-green-600" />
                  I tuoi contenuti
                </h3>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 inline-flex items-center justify-center w-12 h-12 bg-green-50 text-green-600 rounded-full mr-4 shadow-sm border border-green-100">
                      <Package size={20} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Accedi ai tuoi contenuti esclusivi</p>
                    </div>
                  </div>
                  <Link 
                    to="/contenuti" 
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-full shadow-sm hover:shadow-md hover:bg-green-700 transition-all"
                  >
                    <BookOpen size={14} className="mr-2" />
                    Visualizza Contenuti
                  </Link>
                </div>
              </div>
            </div>
            <div className="hidden md:flex p-10 items-center justify-center">
              <div className="bg-white p-6 rounded-full shadow-sm border border-green-50 relative">
                <User size={80} className="text-green" />
                <div className="absolute -top-1 -right-1 bg-green-50 p-2 rounded-full border border-green-100">
                  <Heart size={20} className="text-green-600" />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Unified Content & Actions Card - Now just for quick actions */}
        <div className="bg-gradient-to-r from-green-50 to-white rounded-xl shadow-sm overflow-hidden mb-8 border border-green-100">
          <div className="p-6 border-b border-green-100">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <RefreshCw size={20} className="mr-2 text-green-600" />
              Azioni rapide
            </h3>
          </div>
          
          <div className="p-6">
            <div className="flex flex-wrap gap-3">
              
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-5 py-3 border border-red-100 rounded-full shadow-sm text-sm font-medium text-white bg-red-500 hover:bg-red-600 hover:shadow-md transition-all"
              >
                <LogOut size={16} className="mr-2" />
                Disconnetti
              </button>
              
              <button 
                onClick={refreshUserInfo}
                className="inline-flex items-center px-5 py-3 border border-gray-200 rounded-full shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:shadow-md transition-all"
                disabled={isRefreshing}
              >
                <RefreshCw size={16} className={`mr-2 text-green-600 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Aggiorna' : 'Aggiorna'}
              </button>
            </div>
          </div>
        </div>
        
        {/* Personal Info - Kept at BOTTOM */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <User size={20} className="mr-2 text-green-600" />
              Informazioni Personali
            </h3>
          </div>
          <div className="p-8">
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
              <div className="space-y-5">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <p className="text-gray-800 font-medium bg-white py-2 px-4 rounded-lg border border-gray-100">
                    {user.email}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Nome</p>
                  <p className="text-gray-800 font-medium bg-white py-2 px-4 rounded-lg border border-gray-100">
                    {profile?.name && profile?.surname ? `${profile.name} ${profile.surname}` : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;